import React, { Suspense, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";

import AssessmentPage from "../pages/assessment-candidate";
import AssessmentFeedbackPage from "../pages/assessment-candidate/feedback";
import ProtectedRoutes from "@lxp-app/routes/guards/protectedRoutes";
import AssessmentAccessControlGuard from "./guards/assessmentAccessControlGuard";
import {
  getAllowedAuthoringRoutes,
  getAllowedSuperAdminRoutes,
  SuperAdminRedirect
} from "@assessment/routes/guards/authoringRouteGuard";
// import LandingPage from "../pages/landing-page/LandingPage";
import OrgGuardLayout from "@lxp-app/routes/guards/org-routes-config/orgRouteGuard";
import { updateUrl } from "@lxp-app/components/molecules/breadcrumb/slices/breadcrumbSlice";
const AppLoader = React.lazy(
  () => import("@lxp-app/components/atoms/app-loader/appLoader")
);

const LandingPageV2 = React.lazy(
  () => import("@assessment/pages/landing-page/LandingPageV2")
);
const LogoutPage = React.lazy(
  () => import("@lxp-app/pages/logout-page/logoutPage")
);

const AuthoringDashboard = React.lazy(
  () => import("@lxp-app/pages/authoring-dashboard/authoringDashboard")
);

const HomePageSkeletonLoader = React.lazy(
  () => import("@lxp-app/components/atoms/app-loader/homepageSkeletonLoader")
);

const OnboardingRedirectHandler = React.lazy(
  () => import("@lxp-app/routes/guards/onboardingRedirectHandler")
);
const ContentAuthoring = React.lazy(
  () => import("@lxp-app/pages/content-authoring/content-authoring")
);
// const ErrorPage = React.lazy(() => import("@lxp-app/pages/error/errorPage"));
import ErrorPage from "@assessment/pages/error/errorPage";

import { ContentAuthoringRedirect } from "@assessment/routes/guards/authoringRouteGuard";

import AssessmentAuthGuard from "@lxp-app/routes/guards/accessCheck/assessmentAuthGuard";
// import InterviewPage from "@assessment/layouts/InterviewPage";

const InterviewPage = React.lazy(
  () => import("@assessment/pages/interview/InterviewPage")
);

const QuestionGeneration = React.lazy(
  () => import("@assessment/pages/question-generation/QuestionGeneration")
);

const AssessmentCreateDashboard = React.lazy(
  () =>
    import(
      "@lxp-app/pages/assessment-library/assessment-create-dashboard/assessmentCreateDashboard"
    )
);

const AssessmentReview = React.lazy(
  () =>
    import(
      "@lxp-app/pages/assessment-library/assessment-review/assessmentReview"
    )
);

const QuestionBank = React.lazy(
  () => import("@lxp-app/pages/question-authoring/question-bank")
);

const CreateQuestionComponent = React.lazy(
  () => import("@lxp-app/pages/question-authoring/createQuestionPage")
);

const QuestionPreviewPage = React.lazy(
  () => import("@lxp-app/pages/question-authoring/question-preview-page")
);

const QuestionReviewPage = React.lazy(
  () => import("@lxp-app/pages/question-authoring/question-review-page")
);

const BulkUploadHistory = React.lazy(
  () =>
    import(
      "@lxp-app/pages/question-authoring/bulk-upload-question/bulkUploadHistory"
    )
);

const QuestionsBulkHistory = React.lazy(
  () =>
    import("@lxp-app/pages/question-authoring/questions-bulk-action-history")
);

const ExternalUserBulkUploadHistory = React.lazy(
  () =>
    import(
      "@lxp-app/pages/assessment-library/my-assessments/externalUserBulkUploadHistory"
    )
);

const ScheduleMetaData = React.lazy(
  () =>
    import("@lxp-app/pages/assessment-library/my-assessments/scheduleMetaData")
);

const InviteParticipants = React.lazy(
  () =>
    import(
      "@lxp-app/pages/assessment-library/my-assessments/inviteParticipants"
    )
);

const AssessmentReportLibrary = React.lazy(
  () =>
    import(
      "@lxp-app/pages/question-authoring/candidateResultSummaryForAuthoring/assessmentReportLibrary"
    )
);

const PreviewSchedule = React.lazy(
  () =>
    import("@lxp-app/pages/assessment-library/my-assessments/previewSchedule")
);

const ResultViewWithSummary = React.lazy(
  () =>
    import(
      "@lxp-app/pages/question-authoring/candidateResultSummaryForAuthoring/resultViewWithSummary"
    )
);
const NotificationViewer = React.lazy(
  () =>
    import(
      "@lxp-app/components/molecules/notification/notificationDetailedView/notificationViewer"
    )
);

const AssessmentSchedule = React.lazy(
  () =>
    import(
      "@lxp-app/pages/assessment-library/my-assessments/assessmentSchedule.tsx"
    )
);

const AssessmentCreate = React.lazy(
  () => import("@lxp-app/pages/assessment-library/assessmentCreate.tsx")
);

const LearningAdminDashboard = React.lazy(
  () => import("@lxp-app/pages/learning-admin-authoring/learningAdminDashboard")
);

const ManageUsers = React.lazy(
  () => import("@lxp-app/pages/group-authoring/manageUsers")
);

const GroupDetails = React.lazy(
  () => import("@lxp-app/pages/group-authoring/group-details")
);

const AssignRole = React.lazy(
  () => import("@lxp-app/pages/manage-role/assignAndEditRoles/assignRole")
);
const EditRole = React.lazy(
  () => import("@lxp-app/pages/manage-role/assignAndEditRoles/editRole")
);

const MyPreferences = React.lazy(
  () => import("@assessment/pages/my-preferences/myPreferences")
);

export default function AppRoutes() {
  const location = useLocation();
  const dispatch = useDispatch();
  const allowedAuthoringRoutes = getAllowedAuthoringRoutes();
  const allowedSuperAdminRoutes = getAllowedSuperAdminRoutes();

  // onRouteChange((newRoutePath: string) => {
  //   toast.remove();
  //   console.log(JSON.stringify(newRoutePath));
  // });

  useEffect(() => {
    // This function will be called on every route change
    const handleRouteChange = () => {
      console.log(location.pathname, "current route path");
      // alert(location.pathname)
      dispatch(updateUrl(location.pathname));
      toast.remove();
    };
    handleRouteChange();
  }, [location, dispatch]);

  const ErrorRoutes = () => {
    return (
      <Routes>
        <Route path="/:type/:id?" element={<ErrorPage />} />
      </Routes>
    );
  };

  // if (isAuthLoading) {
  //   return <AppLoader />;
  // }

  return (
    <Routes>
      <Route element={<OrgGuardLayout />}>
        <Route element={<ProtectedRoutes />}>
          <Route
            path="/"
            element={
              <Suspense fallback={<HomePageSkeletonLoader />}>
                <LandingPageV2 />
              </Suspense>
            }
          />
          <Route path="/hitachi" element={<Navigate to="/" replace />} />
          <Route
            path="/authoring/assessment-authoring/assessment-report/:scheduleId/:userId/:ongoingId"
            element={<ResultViewWithSummary />}
          />
          <Route element={<AssessmentAccessControlGuard />}>
            <Route
              path="/assessment/:scheduleId"
              element={<AssessmentPage />}
            />
            <Route
              path="/assessment/feedback/:scheduleId"
              element={<AssessmentFeedbackPage />}
            />
          </Route>
          <Route path="/notifications" element={<NotificationViewer />} />

          {/* Admin */}
          <Route element={<OnboardingRedirectHandler />}>
            <Route
              path="/authoring/assessment-authoring"
              element={<AssessmentSchedule />}
            />
            <Route
              path="/authoring/question-paper-authoring"
              element={<AssessmentCreate />}
            />
            <Route
              path="/authoring/content-authoring/:contentType/:contentId"
              element={<ContentAuthoring />}
            />
          </Route>
          <Route element={<AssessmentAuthGuard />}>
            <Route
              path="/authoring/question-paper-authoring/create-qp/:id/:version"
              element={<AssessmentCreateDashboard />}
            />
            <Route
              path="/authoring/question-paper-authoring/review-qp/:id/:version"
              element={<AssessmentReview />}
            />
            <Route
              path="/authoring/question-paper-authoring/preview-qp/:id/:version"
              element={<AssessmentReview />}
            />
            <Route
              path="/authoring/question-paper-authoring/question-authoring"
              element={<QuestionBank />}
            />
            <Route
              path="/authoring/question-paper-authoring/question-authoring/:subType/:id/:version"
              element={<CreateQuestionComponent />}
            />
            <Route
              path="/authoring/question-paper-authoring/question-authoring/:subType/:id/:version/question-preview"
              element={<QuestionPreviewPage />}
            />
            <Route
              path="/authoring/question-paper-authoring/question-authoring/:subType/:id/:version/previewandpublish"
              element={<QuestionPreviewPage />}
            />
            <Route
              path="/authoring/question-paper-authoring/question-authoring/question-review"
              element={<QuestionReviewPage />}
            />
            <Route
              path="/authoring/question-paper-authoring/question-authoring/bulk-upload-history"
              element={<BulkUploadHistory />}
            />
            <Route
              path="/authoring/question-paper-authoring/question-authoring/questions-bulk-action-history"
              element={<QuestionsBulkHistory />}
            />
            <Route
              path="/authoring/assessment-authoring/external-user-bulk-upload-history/:id"
              element={<ExternalUserBulkUploadHistory />}
            />
            <Route
              path="/authoring/assessment-authoring/assessment-schedule/:id"
              element={<ScheduleMetaData />}
            />
            <Route
              path="/authoring/assessment-authoring/invite-participants/:id"
              element={<InviteParticipants />}
            />
            <Route
              path="/authoring/assessment-authoring/assessment-report/:id"
              element={<AssessmentReportLibrary />}
            />
            <Route
              path="/authoring/assessment-authoring/preview-schedule/:id"
              element={<PreviewSchedule />}
            />
          </Route>
          <Route path="/interview" element={<InterviewPage />} />
          <Route path="/generate-questions" element={<QuestionGeneration />} />
          <Route path="/super-admin">
            <Route index element={<SuperAdminRedirect />} />
            {allowedSuperAdminRoutes.map(({ path, tab }) => (
              <Route
                key={path}
                path={path}
                element={<LearningAdminDashboard defaultTabStateValue={tab} />}
              />
            ))}
          </Route>
          <Route
            path="/super-admin/user-group-authoring/:id/manage-users"
            element={<ManageUsers />}
          />
          <Route
            path="/super-admin/user-group-authoring/:id"
            element={<GroupDetails />}
          />
          <Route path="super-admin/assign-role" element={<AssignRole />} />
          <Route path="super-admin/edit-role" element={<EditRole />} />
          <Route path="/my-preferences" element={<MyPreferences />} />

          <Route path="/error/*" element={<ErrorRoutes />} />
          <Route path="*" element={<Navigate to="/error/page-not-found" />} />
        </Route>
        <Route path="/logout" element={<LogoutPage />} />
      </Route>
    </Routes>
  );
}
